import React, { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Form, Input, Select, message } from "antd";
import { UiButton } from "../../ui";
import AsyncSearchSelect from "../common/AsyncSearchSelect";
import styles from "./saleEntry.module.css";
import { toId } from "../common/ids";
import { formatMoney } from "../common/money";
import BillPickerModal, { isBillOpen } from "./BillPickerModal";
import { getIpAdmissionApi } from "../../../services/receptionist.services";
import { getReceptionistOutPatientListApi } from "../../../services/receptionistop.services";
import { getAdmissionApi } from "../../../services/op.service";
import {
  addPharmacyOrderApi,
  updatePharmacyOrderApi,
  searchPharmacyPatientsApi,
} from "../../../services/pharmacyOrders.services";
import { searchCounterSaleCustomersApi } from "../../../services/pharmacyMedicines.services";
import { getPharmacyBillsForCustomerApi } from "../../../services/pharmacyBilling.services";
import { getCookie } from "../../../helpers/localStorage";
import { UserContext } from "../../../context/UserContext";
import { FacilityID } from "../../../constants/defaultKeys";


const resolveFacilityId = (userDetails) => {
  const fromCookie = getCookie(FacilityID);
  if (fromCookie && fromCookie !== "null" && fromCookie !== "undefined") {
    return fromCookie;
  }
  const activeOrg = userDetails?.activeOrg;
  const fromRole = activeOrg?.roles?.find((r) => r?.facility_id)?.facility_id;
  if (fromRole) return fromRole;
  const fromFacilities = activeOrg?.facilities?.find(
    (f) => f?.facility_id
  )?.facility_id;
  return fromFacilities ?? userDetails?.currFacility ?? null;
};

const buildQuery = (params) =>
  new URLSearchParams(
    Object.entries(params).filter(
      ([, v]) => v !== null && v !== undefined && v !== "" && v !== "null"
    )
  );

const GENDER_OPTIONS = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" },
];

const listRows = (res) => {
  const body = res?.data;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  if (Array.isArray(body?.results)) return body.results;
  return [];
};

const fetchPatients = (text) =>
  searchPharmacyPatientsApi(new URLSearchParams({ query: text })).then((res) =>
    listRows(res).map((item) => ({
      value: item.id,
      label: `${item.name || `${item.first_name || ""} ${item.last_name || ""}`.trim()}${
        item.mobile ? ` · ${item.mobile}` : ""
      }`,
      raw: item,
    }))
  );

const fetchIpAdmissions = (text, facilityId) =>
  getIpAdmissionApi(
    buildQuery({
      facility: facilityId,
      list: "active",
      search: text,
      limit: 20,
    })
  ).then((res) =>
    listRows(res).map((item) => ({
      value: item.id,
      label: `${item.patient_name || "-"} - ${item.ip_id || item.id}`,
      raw: item,
    }))
  );

const fetchOpAdmissions = (text, facilityId) =>
  getReceptionistOutPatientListApi(
    buildQuery({ facility: facilityId, search: text, limit: 20 })
  ).then((res) =>
    listRows(res).map((item) => ({
      value: item.id,
      label: `${item.patient_name || "-"} - ${item.op_id || item.id}`,
      raw: item,
    }))
  );

/* The last row of the customer list, which starts a walk-in instead. */
const NEW_COUNTER_CUSTOMER = "__new";

/** "₹340 open", when the endpoint says what a customer still owes. */
const openBillsLabel = (item) => {
  const amount = Number(
    item?.open_amount ?? item?.due_amount ?? item?.outstanding_amount ?? NaN
  );
  return Number.isFinite(amount) && amount > 0
    ? `${formatMoney(amount)} open`
    : null;
};

/*
 * Who the counter sale is for, as one list rather than a suggestion on each box.
 *
 * Uses the counter-sale-customers endpoint rather than the older
 * /pharmacy/search/counter-sale/ alias: only this one is documented to return
 * the customer id and last_doctor_* details the doctor fields prefill from.
 *
 * Each row reads "Name · Mobile", which is the format the mockup uses and the
 * only one that identifies a person: two customers can share a name, and a
 * mobile on its own says nothing about whose it is.
 */
const fetchCounterSaleCustomers = (text) =>
  searchCounterSaleCustomersApi(
    new URLSearchParams({ query: text || "", field: "name" })
  ).then((res) => {
    const rows = listRows(res).map((item, index) => ({
      value: item.id ?? `${item.name}-${item.mobile}-${index}`,
      label: [item.name, item.mobile, openBillsLabel(item)]
        .filter(Boolean)
        .join(" · "),
      name: item.name,
      mobile: item.mobile,
      gender: item.gender,
      // Derived server-side from the customer's most recent order.
      // Tolerate either naming, so a serializer that returns the plain
      // doctor_* fields still prefills.
      lastDoctorName: item.last_doctor_name ?? item.doctor_name,
      lastDoctorSpecialization:
        item.last_doctor_specialization ?? item.doctor_specialization,
      lastDoctorRegistrationNumber:
        item.last_doctor_registration_number ?? item.doctor_registration_number,
    }));
    return [...rows, { value: NEW_COUNTER_CUSTOMER, label: "New customer…" }];
  });

/*
 * What a blank sale entry opens on. Kept next to the buttons it has to agree
 * with: this is the first of them, so the form and the highlighted button say
 * the same thing when the screen opens.
 */
const SALE_TYPES = [
  { value: "IP", label: "IP" },
  { value: "OP", label: "OP" },
  { value: "PATIENT", label: "Patient" },
  { value: "COUNTER", label: "Counter Sale" },
];
const DEFAULT_SALE_TYPE = SALE_TYPES[0].value;

const BILL_SALE_TYPES = ["PATIENT", "COUNTER"];

/*
 * Who the sale turned out to be for, read back under the search that found
 * them. Deliberately not fields: nothing in this strip invites an edit, because
 * none of it is this screen's to change.
 */
const DetailItem = ({ label, value }) => (
  <span>
    <span className={styles.k}>{label}</span>
    <span className={styles.v}>{value || "-"}</span>
  </span>
);

/*
 * The patient's primary key, out of whichever shape the admission came back in.
 * The OP detail endpoint nests the patient as an object; the IP list endpoint
 * flattens it and leaves the raw foreign key behind. Anything else means the
 * record cannot say who it is for, and the caller gets null rather than a
 * half-formed query string.
 */
/* Where a record names the patient outright. Tried in order, before the
   `*_id` convention below. */
const PATIENT_KEYS = ["patient", "patient_pk", "patient_fk", "patient_uuid"];

/*
 * The patient's primary key, out of whichever shape the record arrived in.
 *
 * `patient_id` has to be taken carefully: on an IP admission row it is the
 * foreign key, sitting alongside `facility_id` and `doctor_id` in the same
 * convention, with the human-readable number kept apart in `patient_mrn`. But
 * inside a nested patient object the same name holds the MRN instead. So it is
 * only trusted when the record keeps its MRN somewhere else - otherwise the
 * name is doing the other job and using it would look up the wrong person.
 */
const patientPk = (record) => {
  if (!record) return null;

  for (const key of PATIENT_KEYS) {
    const id = toId(record[key]);
    if (id !== null) return id;
  }

  const raw = record.patient_id;
  const mrnHeldSeparately = Boolean(record.patient_mrn ?? record.mrn ?? record.uhid);
  if (typeof raw === "number" || (mrnHeldSeparately && toId(raw) !== null)) {
    return toId(raw);
  }

  return null;
};

const deriveDemographics = (saleType, recap) => {
  const empty = { patientName: "", patientId: "", age: "", gender: "", admissionNo: "", bedNo: "", mobile: "" };
  if (!recap) return empty;

  if (saleType === "IP") {
    return {
      patientName: recap.patient_name || "",
      patientId: recap.patient_mrn || "",
      age: recap.patient_age_by_dob != null ? String(recap.patient_age_by_dob) : recap.patient_age || "",
      gender: recap.patient_gender || "",
      admissionNo: recap.ip_id || "",
      bedNo: recap.bed_name || "",
      mobile: recap.contact_no || "",
    };
  }

  if (saleType === "OP") {
    const patient = recap.patient;
    return {
      patientName: patient?.name || recap.patient_name || "",
      // recap.patient_id (from the OP search list endpoint) is OPAdmission's raw FK/PK,
      // not the patient's MRN - recap.patient_mrn (or the nested patient.patient_id from
      // the saved-order recap fetch) is the actual human-readable Patient ID.
      patientId: patient?.patient_id || recap.patient_mrn || "",
      age:
        patient?.age_by_dob != null
          ? String(patient.age_by_dob)
          : recap.patient_age_by_dob != null
            ? String(recap.patient_age_by_dob)
            : recap.patient_age || "",
      gender: patient?.gender || recap.patient_gender || "",
      admissionNo: recap.op_id || "",
      bedNo: "",
      mobile: recap.contact_no || patient?.mobile || "",
    };
  }

  if (saleType === "PATIENT") {
    return {
      patientName: recap.name || `${recap.first_name || ""} ${recap.last_name || ""}`.trim(),
      patientId: recap.patient_id || "",
      age: recap.age_by_dob != null ? String(recap.age_by_dob) : recap.age != null ? String(recap.age) : "",
      gender: recap.gender || "",
      admissionNo: "",
      bedNo: "",
      mobile: recap.mobile || "",
    };
  }

  return empty;
};

const PatientDetailsCard = forwardRef(({ order, onSaved, onSaleTypeChange, onPatientChange, onCarriedBillChange, disabled }, ref) => {
  const { userDetails } = useContext(UserContext);
  const facilityId = resolveFacilityId(userDetails);
  const [form] = Form.useForm();
  const [recap, setRecap] = useState(null);
  const [saving, setSaving] = useState(false);
  const saleType =
    Form.useWatch("sale_type", form) ?? order?.sale_type ?? DEFAULT_SALE_TYPE;
  const patientValue = Form.useWatch("patient", form);
  const counterCustomerValue = Form.useWatch("counter_sale_customer", form);
  const [openBills, setOpenBills] = useState([]);
  /* Which customer `openBills` is the answer for - null while it is in flight. */
  const [billsLoadedFor, setBillsLoadedFor] = useState(null);
  const [billModalOpen, setBillModalOpen] = useState(false);
  /* The customer the bill prompt was already shown for, so it asks once. */
  const promptedForRef = useRef(null);
  const ipAdmissionValue = Form.useWatch("ip_admission", form);
  const opConsultationValue = Form.useWatch("op_consultation", form);
  const saleTypeLocked = Boolean(order?.id);

  /*
   * Which order the form was last filled from. `undefined` means it has never
   * been filled - a blank new entry.
   */
  const filledFromRef = useRef(undefined);

  useEffect(() => {
    const fromOrder = {
      sale_type: order?.sale_type || DEFAULT_SALE_TYPE,
      patient: order?.patient,
      ip_admission: order?.ip_admission,
      op_consultation: order?.op_consultation,
      counter_sale_customer: order?.counter_sale_customer,
      counter_sale_name: order?.counter_sale_customer_name,
      counter_sale_mobile: order?.counter_sale_customer_mobile,
      counter_sale_gender: order?.counter_sale_customer_gender,
      doctor_name: order?.doctor_name,
      doctor_specialization: order?.doctor_specialization,
      doctor_registration_number: order?.doctor_registration_number,
    };

    /*
     * A different order means a different patient, so everything it does not
     * carry has to be cleared or the previous one's details would linger.
     *
     * The first save of a new entry is not that: the order's id appears, this
     * runs, and the form on screen is where those details came from in the
     * first place. Writing the response over it blanked Name, Mobile and Gender
     * whenever the create response left them out, while the doctor fields -
     * which it does return - stayed put. Only values the server actually sent
     * are applied in that case, so nothing typed is lost.
     */
    const isAnotherOrder =
      filledFromRef.current !== undefined && filledFromRef.current !== order?.id;

    form.setFieldsValue(
      isAnotherOrder
        ? fromOrder
        : Object.fromEntries(
            Object.entries(fromOrder).filter(
              ([, value]) => value !== undefined && value !== null
            )
          )
    );

    filledFromRef.current = order?.id;
  }, [order?.id]);

  useEffect(() => {
    /*
     * Also runs before the order exists, so an entry opened from the header
     * search with a patient already chosen shows that patient's details
     * straight away rather than only after the first save.
     */
    if (order?.id || order?.ip_admission || order?.op_consultation || order?.patient) {
      if (order.sale_type === "IP" && order.ip_admission) {
        getIpAdmissionApi(
          buildQuery({
            facility: order.facility || facilityId,
            list: "all",
            admission_id: order.ip_admission,
          })
        )
          .then((res) => setRecap(listRows(res)?.[0] || null))
          .catch(() => setRecap(null));
      } else if (order.sale_type === "OP" && order.op_consultation) {
        getAdmissionApi(order.op_consultation)
          .then((res) => setRecap(res?.data || null))
          .catch(() => setRecap(null));
      } else if (order.sale_type === "PATIENT") {
        setRecap({
          name: order.patient_name,
          first_name: order.patient_name,
          patient_id: order.patient_mrn,
        });
      } else {
        setRecap(null);
      }
    }
  }, [order?.id, order?.sale_type, order?.ip_admission, order?.op_consultation, order?.patient]);

  /*
   * Whose open bills to offer. A counter sale only has one once the customer
   * was picked from the suggestions - a walk-in typed by hand is new, and has
   * nothing to add onto.
   */
  const billCustomer = BILL_SALE_TYPES.includes(saleType)
    ? (saleType === "PATIENT"
        ? patientValue ?? order?.patient
        : counterCustomerValue ?? order?.counter_sale_customer) || null
    : null;

  /*
   * The bill is fixed when the order is created, so the picker is only offered
   * on an entry that does not exist yet; afterwards the order just states the
   * bill it went onto.
   */
  const canPickBill = !order?.id && Boolean(billCustomer);

  useEffect(() => {
    if (!canPickBill) {
      setOpenBills([]);
      setBillsLoadedFor(null);
      return undefined;
    }

    let cancelled = false;
    /*
     * Whatever is in hand belongs to the customer before this one. Left there,
     * the prompt below sees a non-empty list and opens on somebody else's bills
     * - which then empties under it the moment the fetch lands.
     */
    setOpenBills([]);
    setBillsLoadedFor(null);

    getPharmacyBillsForCustomerApi(
      new URLSearchParams(
        saleType === "PATIENT"
          ? { patient: billCustomer }
          : { counter_sale_customer: billCustomer }
      )
    )
      .then((res) => {
        if (cancelled) return;
        setOpenBills(listRows(res));
        setBillsLoadedFor(billCustomer);
      })
      .catch(() => {
        if (cancelled) return;
        setOpenBills([]);
        setBillsLoadedFor(billCustomer);
      });

    return () => {
      cancelled = true;
    };
  }, [canPickBill, billCustomer, saleType]);

  /*
   * Nothing to choose between unless this customer has a bill that can still be
   * added to, so none of this shows rather than prompting over an empty list -
   * or over one where every row is closed and the only way out is Cancel.
   *
   * Gated on the list having been loaded for this customer, not merely on it
   * being non-empty: until then it is the last customer's answer.
   */
  const showBillPicker =
    canPickBill &&
    billsLoadedFor === billCustomer &&
    openBills.some(isBillOpen);
  const selectedBillId = Form.useWatch("bill", form);
  const selectedBill = openBills.find((b) => b.id === selectedBillId);

  /*
   * The bill this sale is being added to, reported up so the billing card can
   * lead with what is already owed on it - the pharmacist is collecting the
   * whole bill, not this entry's share of it.
   *
   * Sent as a flat pair so a re-render that changed nothing does not restate
   * it.
   */
  const carriedRef = useRef("");
  useEffect(() => {
    if (!onCarriedBillChange) return;
    const amount = Number(selectedBill?.grand_total ?? selectedBill?.total_amount) || 0;
    const signature = selectedBill ? `${selectedBill.id}|${amount}` : "";
    if (carriedRef.current === signature) return;
    carriedRef.current = signature;
    onCarriedBillChange(
      selectedBill ? { id: selectedBill.id, number: selectedBill.bill_number, amount } : null
    );
  }, [selectedBill]);

  // A bill belongs to the customer it was picked for and to nobody else.
  useEffect(() => {
    form.setFieldValue("bill", undefined);
    if (!billCustomer) promptedForRef.current = null;
  }, [billCustomer, form]);

  /*
   * Asked as soon as the customer is known, not left for the pharmacist to
   * notice: the bill is fixed when the order is created, so after that it is
   * too late to put this order on an existing bill.
   */
  useEffect(() => {
    if (!showBillPicker || promptedForRef.current === billCustomer) return;
    promptedForRef.current = billCustomer;
    setBillModalOpen(true);
  }, [showBillPicker, billCustomer]);

  const demographics = deriveDemographics(saleType, recap);
  const hasSelection =
    (saleType === "IP" && (ipAdmissionValue || order?.ip_admission)) ||
    (saleType === "OP" && (opConsultationValue || order?.op_consultation)) ||
    (saleType === "PATIENT" && (patientValue || order?.patient));

  /*
   * `bill` is only accepted when the order is created - the server rejects it
   * on an update - so it is left out of every save but the first.
   */
  const buildPayload = (values, { creating }) => {
    const payload = { sale_type: values.sale_type };
    if (creating && values.bill && BILL_SALE_TYPES.includes(values.sale_type)) {
      payload.bill = values.bill;
    }
    if (values.sale_type === "COUNTER") {
      if (values.counter_sale_customer) {
        payload.counter_sale_customer = values.counter_sale_customer;
      } else {
        payload.counter_sale_name = values.counter_sale_name;
        payload.counter_sale_mobile = values.counter_sale_mobile;
        payload.counter_sale_gender = values.counter_sale_gender;
      }
      payload.doctor_name = values.doctor_name || undefined;
      payload.doctor_specialization = values.doctor_specialization || undefined;
      payload.doctor_registration_number =
        values.doctor_registration_number || undefined;
    } else if (values.sale_type === "IP") {
      payload.ip_admission = values.ip_admission;
    } else if (values.sale_type === "OP") {
      payload.op_consultation = values.op_consultation;
    } else if (values.sale_type === "PATIENT") {
      payload.patient = values.patient;
    }
    return payload;
  };

  const persist = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const creating = !order?.id;
      const payload = buildPayload(values, { creating });
      const apiCall = creating
        ? addPharmacyOrderApi(payload)
        : updatePharmacyOrderApi(order.id, payload);
      const res = await apiCall;
      onSaved?.(res.data);
      return res.data;
    } catch (err) {
      if (err?.errorFields) throw err;
      const data = err?.response?.data;
      /*
       * A refused bill comes back as {"bill": "..."} rather than a message -
       * wrong facility, cancelled, or a bill belonging to someone else - and
       * that reason is the only thing that tells the pharmacist what to change.
       */
      const billError = Array.isArray(data?.bill) ? data.bill[0] : data?.bill;
      message.error(billError || data?.message || "Failed to save patient details");
      throw err;
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({
    ensureSaved: persist,
    isSaving: () => saving,
  }));

  const handleSaleTypeChange = (e) => {
    const value = e?.target ? e.target.value : e;
    form.setFieldsValue({
      sale_type: value,
      bill: undefined,
      patient: undefined,
      ip_admission: undefined,
      op_consultation: undefined,
      counter_sale_customer: undefined,
      counter_sale_name: undefined,
      counter_sale_mobile: undefined,
      counter_sale_gender: undefined,
      doctor_name: undefined,
      doctor_specialization: undefined,
      doctor_registration_number: undefined,
    });
    setRecap(null);
  };


  /*
   * Choosing the customer fills everything the record knows.
   *
   * Every field is written from its own property, so the name and the mobile
   * can never end up holding each other - which is what happened when each box
   * carried its own suggestion list and a customer with no mobile on file fell
   * back to the name.
   */
  const chooseCounterCustomer = (value, option) => {
    if (!value || value === NEW_COUNTER_CUSTOMER) {
      form.setFieldsValue({
        counter_sale_customer: undefined,
        counter_sale_name: "",
        counter_sale_mobile: "",
        counter_sale_gender: undefined,
        doctor_name: undefined,
        doctor_specialization: undefined,
        doctor_registration_number: undefined,
      });
      return;
    }

    form.setFieldsValue({
      counter_sale_customer: value,
      counter_sale_name: option?.name || "",
      counter_sale_mobile: option?.mobile || "",
      counter_sale_gender: option?.gender || undefined,
      doctor_name: option?.lastDoctorName || undefined,
      doctor_specialization: option?.lastDoctorSpecialization || undefined,
      doctor_registration_number:
        option?.lastDoctorRegistrationNumber || undefined,
    });
  };

  /* A customer picked from the list owns their name; a walk-in types their own. */
  const knownCustomer = Boolean(counterCustomerValue);

  const searchPlaceholder =
    saleType === "IP"
      ? "Search IP admission"
      : saleType === "OP"
        ? "Search OP admission"
        : saleType === "PATIENT"
          ? "Search patient"
          : "";

  /*
   * The action bar says what the button will do, and that turns on the sale
   * type - which lives in this form, not on the order, until the first save.
   * Reported up so the bar is right from the first paint of a blank entry.
   */
  useEffect(() => {
    onSaleTypeChange?.(saleType);
  }, [saleType]);

  /*
   * Who the sale is for, as a patient primary key.
   *
   * Only a PATIENT sale holds one on the order itself - IP and OP orders are
   * keyed by the admission, and the patient behind it is only known once the
   * admission has been read back. That read already happens here, for the
   * demographics strip, so the id is lifted out of the same record and reported
   * up rather than fetched a second time. The price history panel is the only
   * thing that wants it, and without this it had nothing to ask about.
   */
  const directPatientId =
    saleType === "PATIENT"
      ? toId(patientValue ?? order?.patient ?? null)
      : patientPk(recap) ?? patientPk(order);

  const patientMrn = demographics.patientId || order?.patient_mrn || "";

  /*
   * The patient behind an admission, when the admission did not name one.
   *
   * An IP admission row carries the patient's details - name, MRN, age - but
   * not always the key they hang off, and the price history is looked up by
   * key. The MRN is on screen either way, so it is resolved through the
   * patient search rather than left unanswerable. Only ever a fallback: a
   * record that does name its patient never gets here.
   */
  const [lookedUpPatientId, setLookedUpPatientId] = useState(null);
  useEffect(() => {
    if (directPatientId !== null || !patientMrn) {
      setLookedUpPatientId(null);
      return undefined;
    }

    let cancelled = false;
    searchPharmacyPatientsApi(new URLSearchParams({ query: patientMrn }))
      .then((res) => {
        if (cancelled) return;
        const rows = listRows(res);
        /* An MRN is unique, so an exact hit is the answer. Falling back to a
           lone result covers a search that returns it under a name this does
           not know; more than one inexact match is ambiguous, and guessing
           there would put another patient's prices on screen. */
        const exact = rows.find((row) =>
          [row.patient_id, row.mrn, row.patient_mrn, row.uhid].some(
            (value) => value && String(value) === String(patientMrn)
          )
        );
        const hit = exact || (rows.length === 1 ? rows[0] : null);
        setLookedUpPatientId(toId(hit?.id ?? hit));
      })
      .catch(() => {
        if (!cancelled) setLookedUpPatientId(null);
      });

    return () => {
      cancelled = true;
    };
  }, [directPatientId, patientMrn]);

  useEffect(() => {
    const id = directPatientId ?? lookedUpPatientId ?? null;
    /*
     * Nothing to look history up by, and the record is on hand - say which keys
     * it did come with. Costs a line in development and saves guessing at the
     * serializer from the outside.
     */
    if (id === null && recap && process.env.NODE_ENV !== "production") {
      console.warn(
        "[SaleEntry] No patient key on this %s record. Keys present:",
        saleType,
        Object.keys(recap).join(", ")
      );
    }
    onPatientChange?.({
      id,
      name: demographics.patientName || order?.patient_name || "",
      mrn: patientMrn,
    });
  }, [
    directPatientId,
    lookedUpPatientId,
    demographics.patientName,
    order?.patient_name,
    patientMrn,
    recap,
    saleType,
  ]);

  const searchLabel =
    saleType === "IP"
      ? "Search IP Admission"
      : saleType === "OP"
        ? "Search OP Admission"
        : "Search Patient";

  return (
    <section className={styles.card}>
      <div className={styles.cardHead}>
        <h3 className={styles.cardTitle}>Patient Details</h3>
      </div>

      <Form
        layout="vertical"
        form={form}
        disabled={disabled}
        className={styles.antdForm}
        initialValues={{
          sale_type: order?.sale_type || DEFAULT_SALE_TYPE,
          patient: order?.patient,
          ip_admission: order?.ip_admission,
          op_consultation: order?.op_consultation,
          counter_sale_customer: order?.counter_sale_customer,
          counter_sale_name: order?.counter_sale_customer_name,
          counter_sale_mobile: order?.counter_sale_customer_mobile,
          counter_sale_gender: order?.counter_sale_customer_gender,
          doctor_name: order?.doctor_name,
          doctor_specialization: order?.doctor_specialization,
          doctor_registration_number: order?.doctor_registration_number,
        }}
      >
        <Form.Item name="bill" hidden>
          <Input />
        </Form.Item>

        {/*
         * The sale type is chosen on the segmented control below, which is a row
         * of plain buttons rather than a field - so the value has to be
         * registered here for the form to own it. `validateFields` returns only
         * the fields that are mounted, and without this one `sale_type` sat in
         * the store unregistered: it came back undefined on every save, and the
         * create went up as an empty body.
         */}
        <Form.Item name="sale_type" hidden rules={[{ required: true }]}>
          <Input />
        </Form.Item>

        <div className={styles.seGrid}>
          {/* Four states, all four always visible: the sale type is chosen
              without opening anything. */}
          <div className={`${styles.ff} ${saleType === "COUNTER" ? styles.sp6 : styles.sp3}`}>
            <label className={styles.fl}>
              Sale Type <i>*</i>
            </label>
            <div className={styles.seSeg}>
              {SALE_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  disabled={disabled || saleTypeLocked}
                  className={saleType === type.value ? styles.on : undefined}
                  onClick={() => handleSaleTypeChange(type.value)}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {saleType === "IP" && (
            <div className={`${styles.ff} ${styles.sp3}`}>
              <label className={styles.fl}>
                {searchLabel} <i>*</i>
              </label>
              <Form.Item
                name="ip_admission"
                rules={[{ required: true, message: "Please select an admission" }]}
              >
                <AsyncSearchSelect
                  fetcher={(text) => fetchIpAdmissions(text, facilityId)}
                  loadOnOpen
                  placeholder={searchPlaceholder}
                  onChange={(value, option) => {
                    form.setFieldValue("ip_admission", value);
                    setRecap(option?.raw || option?.data?.raw || null);
                  }}
                  value={ipAdmissionValue}
                  disabled={disabled || saleTypeLocked}
                  initialOption={
                    order?.ip_admission
                      ? {
                          value: order.ip_admission,
                          // admission_label is what the header search hands over
                          // before the recap has been fetched.
                          label: demographics.admissionNo
                            ? `${demographics.patientName || "Admission"} - ${demographics.admissionNo}`
                            : order.admission_label || "Admission",
                        }
                      : undefined
                  }
                />
              </Form.Item>
            </div>
          )}

          {saleType === "OP" && (
            <div className={`${styles.ff} ${styles.sp3}`}>
              <label className={styles.fl}>
                {searchLabel} <i>*</i>
              </label>
              <Form.Item
                name="op_consultation"
                rules={[{ required: true, message: "Please select an admission" }]}
              >
                <AsyncSearchSelect
                  fetcher={(text) => fetchOpAdmissions(text, facilityId)}
                  loadOnOpen
                  placeholder={searchPlaceholder}
                  onChange={(value, option) => {
                    form.setFieldValue("op_consultation", value);
                    setRecap(option?.raw || null);
                  }}
                  value={opConsultationValue}
                  disabled={disabled || saleTypeLocked}
                  initialOption={
                    order?.op_consultation
                      ? {
                          value: order.op_consultation,
                          label: demographics.admissionNo
                            ? `${demographics.patientName || "Admission"} - ${demographics.admissionNo}`
                            : order.admission_label || "Consultation",
                        }
                      : undefined
                  }
                />
              </Form.Item>
            </div>
          )}

          {saleType === "PATIENT" && (
            <div className={`${styles.ff} ${styles.sp3}`}>
              <label className={styles.fl}>
                {searchLabel} <i>*</i>
              </label>
              <Form.Item
                name="patient"
                rules={[{ required: true, message: "Please select patient" }]}
              >
                <AsyncSearchSelect
                  fetcher={fetchPatients}
                  placeholder={searchPlaceholder}
                  initialOption={
                    order?.patient ? { value: order.patient, label: order.patient_name } : undefined
                  }
                  onChange={(value, option) => {
                    form.setFieldValue("patient", value);
                    setRecap(option?.raw || null);
                  }}
                  value={patientValue}
                  disabled={disabled || saleTypeLocked}
                />
              </Form.Item>
            </div>
          )}

          {saleType === "COUNTER" && (
            <>
              {/* One list, as the mockup has it: the customer is chosen here
                  and the boxes below read back what the record says. */}
              <div className={styles.ff}>
                <label className={styles.fl}>
                  Customer <i>*</i>
                </label>
                <Form.Item name="counter_sale_customer">
                  <AsyncSearchSelect
                    fetcher={fetchCounterSaleCustomers}
                    loadOnOpen
                    placeholder="Choose customer"
                    disabled={disabled}
                    value={counterCustomerValue}
                    onChange={chooseCounterCustomer}
                  />
                </Form.Item>
              </div>

              <div className={styles.ff}>
                <label className={styles.fl}>
                  Name <i>*</i>
                </label>
                <Form.Item
                  name="counter_sale_name"
                  rules={[{ required: true, message: "Please enter name" }]}
                >
                  <Input
                    placeholder="Name"
                    disabled={disabled}
                    readOnly={knownCustomer}
                    style={knownCustomer ? { background: "var(--cg-n-25)" } : undefined}
                  />
                </Form.Item>
              </div>

              <div className={styles.ff}>
                <label className={styles.fl}>Mobile</label>
                <Form.Item name="counter_sale_mobile">
                  <Input placeholder="Mobile" disabled={disabled} />
                </Form.Item>
              </div>

              <div className={styles.ff}>
                <label className={styles.fl}>Gender</label>
                <Form.Item name="counter_sale_gender">
                  <Select options={GENDER_OPTIONS} placeholder="Gender" allowClear />
                </Form.Item>
              </div>

              <div className={styles.ff}>
                <label className={styles.fl}>Doctor Name</label>
                <Form.Item name="doctor_name">
                  <Input placeholder="Prescribing doctor" disabled={disabled} />
                </Form.Item>
              </div>

              <div className={styles.ff}>
                <label className={styles.fl}>Specialization</label>
                <Form.Item name="doctor_specialization">
                  <Input placeholder="Specialization" disabled={disabled} />
                </Form.Item>
              </div>

              <div className={styles.ff}>
                <label className={styles.fl}>Registration Number</label>
                <Form.Item name="doctor_registration_number">
                  <Input placeholder="Registration number" disabled={disabled} />
                </Form.Item>
              </div>
            </>
          )}

          {/*
            * What the prompt settled on, and the way back to it. Only shown when
            * the customer has a bill open, and gone once the order exists - the
            * choice is only made at creation.
            */}
          {showBillPicker && (
            <div className={`${styles.ff} ${styles.sp3}`}>
              <label className={styles.fl}>Bill</label>
              <div style={{ display: "flex", alignItems: "center", gap: 4, height: 40 }}>
                <strong style={{ fontSize: 14 }}>
                  {selectedBill ? selectedBill.bill_number : "New bill"}
                </strong>
                <UiButton
                  variant="text"
                  size="sm"
                  disabled={disabled}
                  onClick={() => setBillModalOpen(true)}
                >
                  Change
                </UiButton>
              </div>
            </div>
          )}
        </div>

        {/* Once created, the order simply says which bill it sits on. */}
        {(order?.bill_number || (saleType !== "COUNTER" && hasSelection)) && (
          <div className={styles.seDemo}>
            {order?.bill_number ? <DetailItem label="Bill" value={order.bill_number} /> : null}
            {saleType !== "COUNTER" && hasSelection ? (
              <>
                <DetailItem
                  label="Patient Name"
                  value={demographics.patientName || order?.patient_name}
                />
                <DetailItem
                  label="Patient ID"
                  value={demographics.patientId || order?.patient_mrn}
                />
                <DetailItem label="Age" value={demographics.age} />
                <DetailItem label="Gender" value={demographics.gender} />
                {(saleType === "IP" || saleType === "OP") && (
                  <DetailItem label="Admission No" value={demographics.admissionNo} />
                )}
                {saleType === "IP" && <DetailItem label="Bed No" value={demographics.bedNo} />}
                {demographics.mobile ? (
                  <DetailItem label="Mobile" value={demographics.mobile} />
                ) : null}
              </>
            ) : null}
          </div>
        )}
      </Form>

      <BillPickerModal
        open={billModalOpen}
        bills={openBills}
        selectedBillId={selectedBillId}
        customerName={demographics.patientName || form.getFieldValue("counter_sale_name") || ""}
        customerMobile={demographics.mobile || form.getFieldValue("counter_sale_mobile") || ""}
        onSelect={(billId) => {
          form.setFieldValue("bill", billId);
          setBillModalOpen(false);
        }}
        onCreateNew={() => {
          form.setFieldValue("bill", undefined);
          setBillModalOpen(false);
        }}
        // Dismissing settles on nothing, which is a new bill - the same
        // outcome as the button, so closing cannot leave it half-decided.
        onCancel={() => setBillModalOpen(false)}
      />
    </section>
  );
});

export default PatientDetailsCard;
